import { useState, useMemo } from 'react';
import {
  Download,
  Package,
  AlertTriangle,
  TrendingDown,
  Loader2,
  Search,
  Printer,
  Filter,
  ShoppingCart,
  MapPin,
  ArrowRight,
  Minus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useReorderList, type ReorderItem } from '@/hooks/useReorderList';
import { useLocations } from '@/hooks/useLocations';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { toast } from 'sonner';

export default function ReorderListReport() {
  const { organization } = useAuth();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Reorder List' });

  const [selectedLocationId, setSelectedLocationId] = useState<string>('all');
  const [comparisonLocationId, setComparisonLocationId] = useState<string>('none');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['categories', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      // categories are location-scoped (no organization_id column); resolve the
      // org's categories through their location.
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, location:locations!inner(organization_id)')
        .eq('location.organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch suppliers
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  const {
    reorderItems,
    summary,
    groupedByVendor,
    isLoading,
    error,
  } = useReorderList({
    locationId: selectedLocationId === 'all' ? undefined : selectedLocationId,
    vendorId: vendorFilter === 'all' ? undefined : vendorFilter,
    categoryId: categoryFilter === 'all' ? undefined : categoryFilter,
    comparisonLocationId: comparisonLocationId === 'none' ? undefined : comparisonLocationId,
  });

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return reorderItems;
    const query = searchQuery.toLowerCase();
    return reorderItems.filter(
      (item) =>
        item.product_name.toLowerCase().includes(query) ||
        (item.sku && item.sku.toLowerCase().includes(query))
    );
  }, [reorderItems, searchQuery]);

  // Toggle item selection
  const toggleSelection = (productId: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedItems(newSelection);
  };

  // Select all items
  const selectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map((item) => item.product_id)));
    }
  };

  // Get selected items
  const selectedItemsList = filteredItems.filter((item) =>
    selectedItems.has(item.product_id)
  );

  // Create PO from selected items
  const handleCreatePO = () => {
    if (selectedItems.size === 0) {
      toast.error('No items selected', {
        description: 'Please select items to add to a purchase order.',
      });
      return;
    }

    // For now, just show what would be created
    // In a real implementation, this would navigate to PO creation with pre-filled items
    const itemsByVendor = selectedItemsList.reduce((acc, item) => {
      const vendorId = item.vendor_id || 'no_vendor';
      if (!acc[vendorId]) {
        acc[vendorId] = [];
      }
      acc[vendorId].push(item);
      return acc;
    }, {} as Record<string, ReorderItem[]>);

    const vendorCount = Object.keys(itemsByVendor).length;
    const itemCount = selectedItems.size;

    toast.success('Create Purchase Orders', {
        description: `Would create ${vendorCount} PO(s) with ${itemCount} items total.`,
      });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!filteredItems.length) return;

    const headers = [
      'Product',
      'SKU',
      'Category',
      'Vendor',
      'Location',
      'QOH',
      'FOH',
      'BOH',
      'In Transit',
      'On Order',
      'Reorder Point',
      'Desired Level',
      'Need',
      'Last Cost',
    ];

    const rows = filteredItems.map((item) => [
      item.product_name,
      item.sku || '',
      item.category_name || '',
      item.vendor_name || '',
      item.location_name,
      item.quantity_on_hand,
      item.quantity_foh,
      item.quantity_boh,
      item.in_transit,
      item.on_order,
      item.reorder_point,
      item.desired_level,
      item.need_to_order,
      item.last_cost.toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reorder-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStockBadge = (item: ReorderItem) => {
    if (item.is_negative) {
      return <Badge variant="destructive">Negative</Badge>;
    }
    if (item.quantity_on_hand <= 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    }
    return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Low Stock</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Reorder List</h1>
          <p className="text-sm text-muted-foreground">
            Items at or below reorder point that need restocking
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          {selectedItems.size > 0 && (
            <Button size="sm" className="gap-2" onClick={handleCreatePO}>
              <ShoppingCart className="h-4 w-4" />
              Create PO ({selectedItems.size})
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Items to Reorder</span>
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summary.totalItems}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Units Needed</span>
              <Package className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summary.totalNeed.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Estimated Cost</span>
              <Package className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summary.totalValue)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Out of Stock</span>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold mt-2 text-red-600">
              {isLoading ? '...' : summary.outOfStock}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Negative Stock</span>
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2 text-amber-600">
              {isLoading ? '...' : summary.negativeItems}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger className="w-[180px]">
            <MapPin className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={comparisonLocationId} onValueChange={setComparisonLocationId}>
          <SelectTrigger className="w-[180px]">
            <ArrowRight className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Compare with..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No comparison</SelectItem>
            {locations
              .filter((loc) => loc.id !== selectedLocationId)
              .map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Select value={vendorFilter} onValueChange={setVendorFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vendors</SelectItem>
            {suppliers?.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories?.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Error loading reorder list. Please try again.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Items Below Reorder Point</CardTitle>
            <CardDescription>
              {filteredItems.length} items need restocking
              {selectedItems.size > 0 && ` (${selectedItems.size} selected)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
                      onCheckedChange={selectAll}
                    />
                  </TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">QOH</TableHead>
                  {comparisonLocationId !== 'none' && (
                    <TableHead className="text-right">Comp. QOH</TableHead>
                  )}
                  <TableHead className="text-right">In Transit</TableHead>
                  <TableHead className="text-right">On Order</TableHead>
                  <TableHead className="text-right">Reorder Pt</TableHead>
                  <TableHead className="text-right">Desired</TableHead>
                  <TableHead className="text-right">Need</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow
                    key={`${item.product_id}-${item.location_id}`}
                    className={item.is_negative ? 'bg-red-50 dark:bg-red-950/20' : ''}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedItems.has(item.product_id)}
                        onCheckedChange={() => toggleSelection(item.product_id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.product_name}</div>
                        {item.sku && (
                          <div className="text-xs text-muted-foreground">{item.sku}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.vendor_name || <Minus className="h-4 w-4" />}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={item.is_negative ? 'text-red-600 font-medium' : ''}>
                        {item.quantity_on_hand}
                      </span>
                    </TableCell>
                    {comparisonLocationId !== 'none' && (
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {(item as any).comparison_qoh || 0}
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums">
                      {item.in_transit > 0 ? (
                        <span className="text-blue-600">{item.in_transit}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {item.on_order > 0 ? (
                        <span className="text-green-600">{item.on_order}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {item.reorder_point}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {item.desired_level}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {item.need_to_order}
                    </TableCell>
                    <TableCell>{getStockBadge(item)}</TableCell>
                  </TableRow>
                ))}
                {filteredItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={comparisonLocationId !== 'none' ? 11 : 10} className="text-center py-8 text-muted-foreground">
                      No items below reorder point
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Vendor Summary */}
      {groupedByVendor.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>By Vendor</CardTitle>
            <CardDescription>Items grouped by supplier for PO creation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedByVendor.map((group) => (
                <div
                  key={group.vendorId}
                  className="p-4 border rounded-lg"
                >
                  <div className="font-medium">{group.vendorName}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {group.items.length} items · {group.totalNeed} units
                  </div>
                  <div className="text-sm font-medium mt-1">
                    Est. {formatCurrency(group.totalValue)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Reorder List</h1>
            <div className="print-meta">
              {selectedLocationId
                ? locations.find((l) => l.id === selectedLocationId)?.name
                : 'All Locations'}
            </div>
          </div>
          <div className="print-meta">Generated on {new Date().toLocaleString()}</div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Items</div>
            <div className="value">{summary.totalItems}</div>
          </div>
          <div className="print-card">
            <div className="label">Units Needed</div>
            <div className="value">{summary.totalNeed}</div>
          </div>
          <div className="print-card">
            <div className="label">Est. Cost</div>
            <div className="value">{formatCurrency(summary.totalValue)}</div>
          </div>
        </div>

        <div className="print-section">
          <table className="print-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Vendor</th>
                <th>QOH</th>
                <th>In Transit</th>
                <th>On Order</th>
                <th>Reorder Pt</th>
                <th>Need</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={`${item.product_id}-${item.location_id}`}>
                  <td>{item.product_name}</td>
                  <td>{item.vendor_name || '-'}</td>
                  <td>{item.quantity_on_hand}</td>
                  <td>{item.in_transit}</td>
                  <td>{item.on_order}</td>
                  <td>{item.reorder_point}</td>
                  <td>{item.need_to_order}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
